import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { config } from '../config.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';

export interface ThemeConfig {
  primaryColor: string;
  accentColor: string;
  radius: number;
}

const DEFAULT_THEME: ThemeConfig = {
  primaryColor: '#1f78ff',
  accentColor: '#0a2a66',
  radius: 12,
};

export function WidgetPreview(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const activeTenantId = useUiStore((s) => s.activeTenantId);
  const setActiveTenant = useUiStore((s) => s.setActiveTenant);
  useEffect(() => {
    if (id && id !== activeTenantId) setActiveTenant(id);
  }, [id, activeTenantId, setActiveTenant]);
  const [theme, setTheme] = useState<ThemeConfig>(DEFAULT_THEME);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const srcDoc = useMemo(() => buildSrcDoc(theme, config.widgetBundleUrl), [theme]);

  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'zen:theme', theme },
      '*',
    );
  }, [theme]);

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <form className="space-y-3 rounded border border-slate-200 bg-white p-4">
        <h2 className="text-xl font-semibold">{t.widget.previewTitle}</h2>
        <label className="block text-sm">
          <span className="block text-slate-600">{t.widget.primaryColor}</span>
          <input
            type="color"
            aria-label={t.widget.primaryColor}
            value={theme.primaryColor}
            onChange={(e) => setTheme((s) => ({ ...s, primaryColor: e.target.value }))}
          />
        </label>
        <label className="block text-sm">
          <span className="block text-slate-600">{t.widget.accentColor}</span>
          <input
            type="color"
            aria-label={t.widget.accentColor}
            value={theme.accentColor}
            onChange={(e) => setTheme((s) => ({ ...s, accentColor: e.target.value }))}
          />
        </label>
        <label className="block text-sm">
          <span className="block text-slate-600">{t.widget.radius}</span>
          <input
            type="range"
            min={0}
            max={32}
            aria-label={t.widget.radius}
            value={theme.radius}
            onChange={(e) => setTheme((s) => ({ ...s, radius: Number(e.target.value) }))}
          />
          <span className="ml-2 text-xs text-slate-500">{theme.radius}px</span>
        </label>
      </form>
      <div className="rounded border border-slate-200 bg-white p-2">
        <iframe
          ref={iframeRef}
          title="widget-preview"
          data-testid="widget-iframe"
          className="h-[500px] w-full"
          srcDoc={srcDoc}
        />
      </div>
    </div>
  );
}

export function buildSrcDoc(theme: ThemeConfig, bundleUrl: string): string {
  const safeBundle = bundleUrl.replace(/"/g, '&quot;');
  return `<!doctype html><html><head><meta charset="utf-8">
<style>
  body { margin:0; font-family: system-ui; }
  :root {
    --zen-primary: ${theme.primaryColor};
    --zen-accent: ${theme.accentColor};
    --zen-radius: ${theme.radius}px;
  }
</style>
</head><body>
<div id="zen-widget"></div>
<script src="${safeBundle}" defer></script>
<script>
  window.addEventListener('message', (e) => {
    if (e.data && e.data.type === 'zen:theme') {
      const r = document.documentElement.style;
      r.setProperty('--zen-primary', e.data.theme.primaryColor);
      r.setProperty('--zen-accent', e.data.theme.accentColor);
      r.setProperty('--zen-radius', e.data.theme.radius + 'px');
    }
  });
</script>
</body></html>`;
}
